import React, { useState, useEffect, useCallback } from 'react';
import { ShieldCheck, X, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import supabase from '../../lib/supabaseClient';

// Per-record price for the retroactive DNC scrub. Mirrors
// DNC_SCRUB_PRICE_PER_RECORD_CENTS on the server (billing.js).
const DNC_SCRUB_PRICE_PER_RECORD = 0.05;

const fmt = (n) => Number(n || 0).toLocaleString('en-US');
const money = (n) => `$${Number(n || 0).toFixed(2)}`;

// Confirmation modal for a retroactive, campaign-level DNC scrub of already-uploaded
// leads. Shows the campaign name, the active (non-opted-out) lead count, and the
// $0.05/record cost. Offers a "scrub all campaigns" option. On confirm it charges the
// card on file (Express) then invokes the campaign-dnc-scrub edge function.
//
// Props:
//   campaign       - the campaign the button was clicked on ({ id, name, tenant_id })
//   apiBase        - Express API base (campaignManagement API_BASE, includes /api)
//   fetchWithCsrf  - authenticated fetch wrapper from campaignManagement
//   onClose        - () => void
export default function CampaignDncScrubModal({ campaign, apiBase, fetchWithCsrf, onClose }) {
  const [scrubAll, setScrubAll] = useState(false);
  const [campaignCount, setCampaignCount] = useState(null);
  const [allCount, setAllCount] = useState(null);
  const [loadingCounts, setLoadingCounts] = useState(true);
  const [countError, setCountError] = useState('');
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const tenantId = campaign?.tenant_id;

  // Load both counts up front so toggling "all campaigns" is instant. Counts use
  // the exact filter the edge function scrubs: tenant + sms_opted_out = false
  // (+ campaign_id for the single-campaign count).
  useEffect(() => {
    if (!tenantId) return;
    let cancelled = false;
    (async () => {
      setLoadingCounts(true);
      setCountError('');
      try {
        const [single, all] = await Promise.all([
          supabase
            .from('leads')
            .select('id', { count: 'exact', head: true })
            .eq('tenant_id', tenantId)
            .eq('campaign_id', campaign.id)
            .eq('sms_opted_out', false),
          supabase
            .from('leads')
            .select('id', { count: 'exact', head: true })
            .eq('tenant_id', tenantId)
            .eq('sms_opted_out', false),
        ]);
        if (cancelled) return;
        if (single.error || all.error) {
          setCountError('Could not load lead counts. Please try again.');
        } else {
          setCampaignCount(single.count || 0);
          setAllCount(all.count || 0);
        }
      } catch (e) {
        if (!cancelled) setCountError('Could not load lead counts. Please try again.');
      } finally {
        if (!cancelled) setLoadingCounts(false);
      }
    })();
    return () => { cancelled = true; };
  }, [tenantId, campaign?.id]);

  const activeCount = scrubAll ? allCount : campaignCount;
  const cost = (activeCount || 0) * DNC_SCRUB_PRICE_PER_RECORD;

  const handleConfirm = useCallback(async () => {
    if (processing) return;
    const count = scrubAll ? allCount : campaignCount;
    const campaignId = scrubAll ? null : campaign.id;

    if (!count || count <= 0) {
      setError('There are no active leads to scrub.');
      return;
    }

    setProcessing(true);
    setError('');
    try {
      // 1) Charge the card on file. A failure here stops everything.
      const chargeRes = await fetchWithCsrf(`${apiBase}/billing/charge-campaign-dnc-scrub`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tenant_id: tenantId, campaign_id: campaignId, lead_count: count }),
      });
      const chargeData = await chargeRes.json().catch(() => ({}));
      if (!chargeRes.ok) {
        throw new Error(chargeData.error || 'The DNC scrub charge could not be processed.');
      }

      // 2) Run the scrub. The payment already succeeded, so surface a distinct
      // message if this step fails so the user knows they were charged.
      const { data: scrubData, error: scrubErr } = await supabase.functions.invoke('campaign-dnc-scrub', {
        body: {
          tenant_id: tenantId,
          campaign_id: campaignId,
          payment_intent_id: chargeData.payment_intent_id,
          charge_usd: chargeData.charge_usd,
          lead_count: count,
        },
      });
      if (scrubErr || scrubData?.error) {
        throw new Error(
          `Your payment went through, but the scrub could not finish (${scrubErr?.message || scrubData?.error}). Our team has been notified and will complete it.`,
        );
      }

      setResult({
        scrubbedCount: scrubData?.scrubbed_count ?? 0,
        leadCount: scrubData?.lead_count ?? count,
        all: scrubAll,
      });
    } catch (e) {
      setError(e.message || 'Something went wrong. Please try again.');
    } finally {
      setProcessing(false);
    }
  }, [processing, scrubAll, allCount, campaignCount, campaign, tenantId, apiBase, fetchWithCsrf]);

  return (
    <div className="fixed inset-0 z-[80] overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="fixed inset-0 bg-black bg-opacity-60" onClick={processing ? undefined : onClose} />
        <div className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl p-6">
          <button
            onClick={onClose}
            disabled={processing}
            className="absolute top-4 right-4 p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 disabled:opacity-40"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="flex items-center justify-center w-12 h-12 rounded-full bg-cyan-100 mx-auto mb-4">
            <ShieldCheck className="w-6 h-6 text-cyan-700" />
          </div>

          {result ? (
            <div className="text-center">
              <div className="flex items-center justify-center gap-2 text-green-700 font-semibold mb-2">
                <CheckCircle2 className="w-5 h-5" /> Scrub complete
              </div>
              <p className="text-sm text-gray-600">
                Checked {fmt(result.leadCount)} active {result.leadCount === 1 ? 'lead' : 'leads'}
                {result.all ? ' across all campaigns' : ` in ${campaign.name}`}.
                Flagged and silenced <strong>{fmt(result.scrubbedCount)}</strong> {result.scrubbedCount === 1 ? 'number' : 'numbers'} on the DNC or litigator lists.
              </p>
              <p className="text-xs text-gray-500 mt-2">
                Flagged leads are opted out and disappear from your active view. Nothing was deleted.
              </p>
              <button
                onClick={onClose}
                className="mt-5 w-full px-4 py-2.5 bg-cyan-700 text-white rounded-lg font-medium hover:bg-cyan-800 transition-colors"
              >
                Done
              </button>
            </div>
          ) : (
            <>
              <h3 className="text-lg font-semibold text-gray-900 text-center mb-1">
                Scrub for DNC and litigators
              </h3>
              <p className="text-sm text-gray-500 text-center mb-4">
                {scrubAll ? 'All campaigns' : campaign.name}
              </p>

              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                {loadingCounts ? (
                  <div className="flex items-center justify-center gap-2 text-sm text-gray-500 py-2">
                    <Loader2 className="w-4 h-4 animate-spin" /> Counting active leads...
                  </div>
                ) : countError ? (
                  <div className="flex items-center gap-2 text-sm text-red-600">
                    <AlertCircle className="w-4 h-4" /> {countError}
                  </div>
                ) : (
                  <div className="text-sm text-gray-700">
                    <div className="flex items-baseline justify-between">
                      <span>Active leads not yet opted out</span>
                      <strong className="text-gray-900">{fmt(activeCount)}</strong>
                    </div>
                    <div className="flex items-baseline justify-between mt-2 pt-2 border-t border-gray-200">
                      <span>{fmt(activeCount)} x {money(DNC_SCRUB_PRICE_PER_RECORD)}</span>
                      <strong className="text-gray-900 text-base">{money(cost)}</strong>
                    </div>
                  </div>
                )}
              </div>

              <label className="flex items-start gap-3 mt-4 text-sm text-gray-800 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={scrubAll}
                  onChange={(e) => setScrubAll(e.target.checked)}
                  disabled={processing}
                  className="mt-0.5 w-4 h-4 rounded border-gray-300 text-cyan-700 focus:ring-cyan-600"
                />
                <span>
                  <span className="font-medium">Scrub all campaigns at once</span>
                  <span className="block text-xs text-gray-500 mt-0.5">
                    Runs across every campaign for this account and charges once for the combined total.
                  </span>
                </span>
              </label>

              <p className="text-xs text-gray-500 mt-4">
                Flagged numbers are opted out (sms_opted_out set true) and removed from your active view. No leads are deleted. Your card on file is charged before the scrub runs.
              </p>

              {error && (
                <div className="flex items-start gap-2 mt-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">
                  <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" /> <span>{error}</span>
                </div>
              )}

              <button
                onClick={handleConfirm}
                disabled={processing || loadingCounts || !!countError || !activeCount}
                className="mt-5 w-full px-4 py-2.5 bg-cyan-700 text-white rounded-lg font-medium hover:bg-cyan-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
              >
                {processing && <Loader2 className="w-4 h-4 animate-spin" />}
                {processing ? 'Processing...' : `Confirm and scrub ${money(cost)}`}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
